import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes,
  FaTimesCircle,
} from "react-icons/fa";
import "./SBSFeedbackProvider.css";

export const SBSFeedbackContext = createContext(null);

const TONE_META = {
  success: {
    title: "All set",
    Icon: FaCheckCircle,
  },
  info: {
    title: "For your review",
    Icon: FaInfoCircle,
  },
  warning: {
    title: "Review needed",
    Icon: FaExclamationTriangle,
  },
  error: {
    title: "Action needed",
    Icon: FaTimesCircle,
  },
};

const normalizeTone = (tone, fallback = "info") =>
  TONE_META[tone] ? tone : fallback;

const getAnchorPosition = (options) => {
  const anchorElement =
    options.anchorElement ||
    options.anchorRef?.current ||
    options.event?.currentTarget ||
    null;

  if (
    !anchorElement ||
    !anchorElement.isConnected ||
    typeof anchorElement.getBoundingClientRect !== "function" ||
    typeof window === "undefined"
  ) {
    return null;
  }

  const rect = anchorElement.getBoundingClientRect();
  const toastWidth = Math.min(390, window.innerWidth - 32);
  const top = Math.max(12, Math.min(window.innerHeight - 132, rect.bottom + 10));
  const left = Math.max(12, Math.min(window.innerWidth - toastWidth - 12, rect.left));

  return { top, left };
};

export function SBSFeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [activeDialog, setActiveDialogState] = useState(null);
  const [promptValue, setPromptValue] = useState("");

  const toastTimersRef = useRef(new Map());
  const toastIdRef = useRef(0);
  const dialogIdRef = useRef(0);
  const dialogQueueRef = useRef([]);
  const activeDialogRef = useRef(null);
  const promptInputRef = useRef(null);
  const primaryActionRef = useRef(null);

  const setActiveDialog = useCallback((dialog) => {
    activeDialogRef.current = dialog;
    setActiveDialogState(dialog);
  }, []);

  const dismissToast = useCallback((toastId) => {
    const timer = toastTimersRef.current.get(toastId);

    if (timer) {
      clearTimeout(timer);
      toastTimersRef.current.delete(toastId);
    }

    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== toastId)
    );
  }, []);

  const showToast = useCallback(
    (options = {}) => {
      const normalizedOptions =
        typeof options === "string" ? { message: options } : options;
      const tone = normalizeTone(normalizedOptions.tone);
      const toastId =
        normalizedOptions.id || `sbs-toast-${Date.now()}-${toastIdRef.current++}`;
      const duration =
        typeof normalizedOptions.duration === "number"
          ? normalizedOptions.duration
          : 5200;
      const contextualPosition = getAnchorPosition(normalizedOptions);

      const toast = {
        id: toastId,
        tone,
        title: normalizedOptions.title || TONE_META[tone].title,
        message: normalizedOptions.message || "",
        helperText: normalizedOptions.helperText || "",
        contextualPosition,
        duration,
      };

      setToasts((currentToasts) => [toast, ...currentToasts].slice(0, 5));

      if (duration > 0) {
        const timer = setTimeout(() => dismissToast(toastId), duration);
        toastTimersRef.current.set(toastId, timer);
      }

      return toastId;
    },
    [dismissToast]
  );

  const enqueueDialog = useCallback(
    (dialog) => {
      if (activeDialogRef.current) {
        dialogQueueRef.current.push(dialog);
        return;
      }

      setActiveDialog(dialog);
    },
    [setActiveDialog]
  );

  const completeDialog = useCallback(
    (result) => {
      const dialog = activeDialogRef.current;

      if (!dialog) return;

      dialog.resolve(result);
      setActiveDialog(dialogQueueRef.current.shift() || null);
    },
    [setActiveDialog]
  );

  const confirm = useCallback(
    (options = {}) =>
      new Promise((resolve) => {
        const tone = normalizeTone(options.tone, "warning");

        enqueueDialog({
          id: `sbs-dialog-${Date.now()}-${dialogIdRef.current++}`,
          type: "confirm",
          tone,
          title: options.title || "Confirm this action?",
          message: options.message || "Please confirm before SBS continues.",
          helperText: options.helperText || "",
          confirmLabel: options.confirmLabel || "Continue",
          cancelLabel: options.cancelLabel || "Cancel",
          resolve,
        });
      }),
    [enqueueDialog]
  );

  const prompt = useCallback(
    (options = {}) =>
      new Promise((resolve) => {
        const tone = normalizeTone(options.tone, "info");

        enqueueDialog({
          id: `sbs-dialog-${Date.now()}-${dialogIdRef.current++}`,
          type: "prompt",
          tone,
          title: options.title || "Add a note",
          message: options.message || "",
          helperText: options.helperText || "",
          placeholder: options.placeholder || "",
          defaultValue: options.defaultValue || "",
          confirmLabel: options.confirmLabel || "Submit",
          cancelLabel: options.cancelLabel || "Cancel",
          inputLabel: options.inputLabel || "",
          rows: options.rows || 4,
          resolve,
        });
      }),
    [enqueueDialog]
  );

  useEffect(() => {
    return () => {
      toastTimersRef.current.forEach((timer) => clearTimeout(timer));
      toastTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    setPromptValue(activeDialog?.defaultValue || "");
  }, [activeDialog?.defaultValue, activeDialog?.id]);

  useEffect(() => {
    if (!activeDialog) return undefined;

    const focusTimer = setTimeout(() => {
      if (activeDialog.type === "prompt") {
        promptInputRef.current?.focus();
        return;
      }

      primaryActionRef.current?.focus();
    }, 40);

    return () => clearTimeout(focusTimer);
  }, [activeDialog]);

  useEffect(() => {
    if (!activeDialog) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      event.preventDefault();
      completeDialog(activeDialog.type === "prompt" ? null : false);
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeDialog, completeDialog]);

  const feedbackValue = useMemo(
    () => ({
      showToast,
      confirm,
      prompt,
      dismissToast,
    }),
    [showToast, confirm, prompt, dismissToast]
  );

  const renderToast = (toast) => {
    const toneMeta = TONE_META[toast.tone] || TONE_META.info;
    const ToneIcon = toneMeta.Icon;
    const contextualStyle = toast.contextualPosition
      ? {
          top: `${toast.contextualPosition.top}px`,
          left: `${toast.contextualPosition.left}px`,
        }
      : undefined;

    return (
      <div
        className={`sbs-feedback-toast sbs-feedback-toast--${toast.tone}${
          toast.contextualPosition ? " sbs-feedback-toast--contextual" : ""
        }`}
        key={toast.id}
        role="status"
        style={contextualStyle}
      >
        <div className="sbs-feedback-toast-icon" aria-hidden="true">
          <ToneIcon />
        </div>

        <div className="sbs-feedback-toast-copy">
          <strong>{toast.title}</strong>
          {toast.message && <p>{toast.message}</p>}
          {toast.helperText && <small>{toast.helperText}</small>}
        </div>

        <button
          type="button"
          className="sbs-feedback-toast-close"
          onClick={() => dismissToast(toast.id)}
          aria-label="Dismiss message"
        >
          <FaTimes aria-hidden="true" />
        </button>
      </div>
    );
  };

  const renderDialog = () => {
    if (!activeDialog) return null;

    const toneMeta = TONE_META[activeDialog.tone] || TONE_META.info;
    const ToneIcon = toneMeta.Icon;
    const isPrompt = activeDialog.type === "prompt";
    const titleId = `${activeDialog.id}-title`;
    const messageId = `${activeDialog.id}-message`;
    const cancelResult = isPrompt ? null : false;

    const handleSubmit = (event) => {
      event.preventDefault();
      completeDialog(isPrompt ? promptValue : true);
    };

    return (
      <div
        className="sbs-feedback-dialog-overlay"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            completeDialog(cancelResult);
          }
        }}
      >
        <form
          className={`sbs-feedback-dialog sbs-feedback-dialog--${activeDialog.tone}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={messageId}
          onSubmit={handleSubmit}
        >
          <div className="sbs-feedback-dialog-mark" aria-hidden="true">
            <ToneIcon />
          </div>

          <div className="sbs-feedback-dialog-copy">
            <span>Selenne Business Suite</span>
            <h2 id={titleId}>{activeDialog.title}</h2>
            {activeDialog.message && (
              <p id={messageId}>{activeDialog.message}</p>
            )}
            {activeDialog.helperText && <small>{activeDialog.helperText}</small>}
          </div>

          {isPrompt && (
            <div className="sbs-feedback-prompt-field">
              {activeDialog.inputLabel && (
                <label htmlFor={`${activeDialog.id}-input`}>
                  {activeDialog.inputLabel}
                </label>
              )}
              <textarea
                id={`${activeDialog.id}-input`}
                ref={promptInputRef}
                value={promptValue}
                onChange={(event) => setPromptValue(event.target.value)}
                placeholder={activeDialog.placeholder}
                rows={activeDialog.rows}
                aria-label={activeDialog.inputLabel || activeDialog.title}
              />
            </div>
          )}

          <div className="sbs-feedback-dialog-actions">
            <button
              type="button"
              className="sbs-feedback-button sbs-feedback-button--secondary"
              onClick={() => completeDialog(cancelResult)}
            >
              {activeDialog.cancelLabel}
            </button>

            <button
              type="submit"
              ref={primaryActionRef}
              className={`sbs-feedback-button sbs-feedback-button--${activeDialog.tone}`}
            >
              {activeDialog.confirmLabel}
            </button>
          </div>
        </form>
      </div>
    );
  };

  const feedbackLayer = (
    <>
      <div className="sbs-feedback-toast-stack" aria-live="polite">
        {toasts
          .filter((toast) => !toast.contextualPosition)
          .map((toast) => renderToast(toast))}
      </div>

      {toasts
        .filter((toast) => toast.contextualPosition)
        .map((toast) => renderToast(toast))}

      {renderDialog()}
    </>
  );

  return (
    <SBSFeedbackContext.Provider value={feedbackValue}>
      {children}
      {typeof document === "undefined"
        ? feedbackLayer
        : createPortal(feedbackLayer, document.body)}
    </SBSFeedbackContext.Provider>
  );
}

export default SBSFeedbackProvider;
